/* eslint-disable react/prop-types */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styled from 'styled-components';
import noop from 'lodash/noop';
import get from 'lodash/get';
import AutoSuggest from 'react-autosuggest';
import TextCropper from '@omilia/text-cropper';

const StyledAddOption = styled.div`
  color: #4a90e2;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.4;
  padding: 10px 15px;
  border: 1px solid #dddddd;
  border-bottom: none;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;

  &:hover {
    color: #ffffff;
    background: #4a90e2;
  }
`;

export const getSuggestionText = suggestion =>
  get(suggestion, 'name', suggestion);

export const getInputValue = value =>
  (value && value.trim().toLowerCase()) || '';

const SuggestionsAutocomplete = ({
  addTag,
  getSuggestions,
  inputProps,
  isNotUnique,
  withAddOption,
  onAddOption,
  onSearch,
  autoOpen,
  suggestionItemComponent: SuggestionItemComponent,
}) => {
  const handleGetSuggestions = useCallback(
    inputValue => getSuggestions(inputValue) || [],
    [getSuggestions],
  );

  const handleChange = (e, { method }) => {
    if (method === 'enter') {
      e.preventDefault();
    } else {
      inputProps.onChange(e);
    }
  };

  const handleBlur = useCallback(() => {
    addTag(inputProps.value);
  }, [addTag, inputProps.value]);

  const handleFocus = useCallback(() => {
    if (autoOpen) {
      onSearch('');
    }
  }, [autoOpen, onSearch]);

  const handleSuggestionSelected = (e, { suggestion }) => {
    addTag(suggestion);
  };

  const shouldRenderSuggestions = useCallback(
    value => (value && value.trim().length > 0) || autoOpen,
    [autoOpen],
  );

  const handelGetSuggestionValue = suggestion => suggestion;

  const handleSuggestionsFetchRequested = useCallback(
    item => {
      onSearch(item.value);
    },
    [onSearch],
  );

  const renderSuggestion = suggestion =>
    SuggestionItemComponent ? (
      <SuggestionItemComponent item={suggestion} />
    ) : (
      <TextCropper text={getSuggestionText(suggestion)} />
    );

  const renderInput = inputProps => (
    <input
      {...inputProps}
      className={cn(inputProps.className, {
        'is-not-unique': isNotUnique,
      })}
    />
  );

  const renderSuggestionsContainer = ({ containerProps, children }) => (
    <div data-test="suggestions-container" {...containerProps}>
      {withAddOption && children && (
        <StyledAddOption data-test="add-option" onClick={onAddOption}>
          {withAddOption}
        </StyledAddOption>
      )}
      {children}
    </div>
  );

  const inputValue = useMemo(() => getInputValue(inputProps.value), [
    inputProps,
  ]);

  return (
    <AutoSuggest
      ref={inputProps.ref}
      autoOpen={autoOpen}
      getSuggestionValue={handelGetSuggestionValue}
      inputProps={{
        ...inputProps,
        onChange: handleChange,
        onBlur: handleBlur,
        onFocus: handleFocus,
        'data-test': 'tag-input-text',
      }}
      renderInputComponent={renderInput}
      renderSuggestion={renderSuggestion}
      renderSuggestionsContainer={renderSuggestionsContainer}
      shouldRenderSuggestions={shouldRenderSuggestions}
      suggestions={handleGetSuggestions(inputValue)}
      onSuggestionsClearRequested={noop}
      onSuggestionSelected={handleSuggestionSelected}
      onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
    />
  );
};

SuggestionsAutocomplete.propTypes = {
  addTag: PropTypes.func,
  autoOpen: PropTypes.bool,
  getSuggestions: PropTypes.func,
  inputProps: PropTypes.shape({
    className: PropTypes.string,
    ref: PropTypes.any,
    value: PropTypes.string,
    onChange: PropTypes.func,
  }),
  isNotUnique: PropTypes.bool,
  suggestionItemComponent: PropTypes.oneOfType([PropTypes.object]),
  withAddOption: PropTypes.string,
  onAddOption: PropTypes.func,
  onSearch: PropTypes.func,
};

SuggestionsAutocomplete.defaultProps = {
  onSearch: noop,
  onAddOption: noop,
  withAddOption: '',
};

export default SuggestionsAutocomplete;
